import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Tabs,
  Tab,
  Card,
  Stack,
  Chip,
} from "@mui/material";
import api from "../../utils/api";
import BillingSummaryCards from "./BillingSummaryCards";
import BillingSummaryOverview from "./BillingSummaryOverview";
import BillingSummaryExport from "./BillingSummaryExport";

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const BillingSummarySection = () => {
  const [data, setData] = useState([]);
  const [typeMap, setTypeMap] = useState({});
  const [subtypeMap, setSubtypeMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    fetchBillingData();
    fetchWorkOrderTypes();
    fetchWorkOrderSubtypes();
  }, []);

  const fetchBillingData = async () => {
    try {
      const response = await api.get("/jonas-export-reports");
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching report data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkOrderTypes = async () => {
    try {
      const response = await api.get("/workorder-types");
      const map = {};
      response.data.forEach((item) => {
        map[item.id] = item.type;
      });
      setTypeMap(map);
    } catch (error) {
      console.error("Error fetching work order types:", error);
    }
  };

  const fetchWorkOrderSubtypes = async () => {
    try {
      const response = await api.get("/workorder-subtypes");
      const map = {};
      response.data.forEach((item) => {
        map[item.id] = item.subtype_name;
      });
      setSubtypeMap(map);
    } catch (error) {
      console.error("Error fetching work order subtypes:", error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#223142",
            mb: 0.5,
          }}
        >
          Billed Work Orders Summary
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#6b778c",
            mb: 2,
          }}
        >
          Review billing performance, explore trends, and export billed work
          order records.
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            label={loading ? "Loading data..." : `${data.length} billed records`}
            sx={{
              borderRadius: "999px",
              fontWeight: 600,
              backgroundColor: "rgba(47,99,173,0.08)",
              color: "#2f63ad",
            }}
          />
          <Chip
            label={tabIndex === 0 ? "Overview active" : "Export active"}
            sx={{
              borderRadius: "999px",
              fontWeight: 600,
              backgroundColor: "rgba(34,49,66,0.06)",
              color: "#223142",
            }}
          />
        </Stack>
      </Box>

      {!loading && (
        <Card
          elevation={0}
          sx={{
            ...adminCardSx,
            p: { xs: 2, md: 3 },
            mb: 3,
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
            >
              Billing Snapshot
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              High-level metrics for billed work orders.
            </Typography>
          </Box>

          <Box sx={{ mt: 2 }}>
            <BillingSummaryCards data={data} />
          </Box>
        </Card>
      )}

      <Card
        elevation={0}
        sx={{
          ...adminCardSx,
          p: { xs: 1.5, md: 2 },
          mb: 3,
        }}
      >
        <Box
          sx={{
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            px: { xs: 0.5, md: 1 },
          }}
        >
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 52,
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px",
                backgroundColor: "#2f63ad",
              },
            }}
          >
            <Tab
              label="Overview"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                minHeight: 52,
                color: "#6b778c",
                "&.Mui-selected": {
                  color: "#223142",
                },
              }}
            />
            <Tab
              label="Export"
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.95rem",
                minHeight: 52,
                color: "#6b778c",
                "&.Mui-selected": {
                  color: "#223142",
                },
              }}
            />
          </Tabs>
        </Box>

        <Box sx={{ pt: 3 }}>
          {tabIndex === 0 && (
            <BillingSummaryOverview
              data={data}
              loading={loading}
              typeMap={typeMap}
              subtypeMap={subtypeMap}
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
            />
          )}

          {tabIndex === 1 && (
            <BillingSummaryExport
              data={data}
              loading={loading}
            />
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default BillingSummarySection;